import React, { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X, ShoppingBag, Instagram } from "lucide-react";
import { NAV, STRAPLINE, FOOTER } from "../../content/home";
import { SmartLink } from "./shared";

const useCart = () => {
  const [cart, setCart] = useState({ count: 0, slug: null });
  useEffect(() => {
    const compute = () => {
      let count = 0, slug = null;
      for (let i = 0; i < localStorage.length; i++) {
        const k = localStorage.key(i);
        if (!k || !k.startsWith("feral_cart_")) continue;
        try {
          const n = Object.values(JSON.parse(localStorage.getItem(k)) || {}).reduce((s, q) => s + (Number(q) || 0), 0);
          if (n > 0) { count += n; slug = slug || k.replace("feral_cart_", ""); }
        } catch (e) { /* ignore */ }
      }
      setCart({ count, slug });
    };
    compute();
    window.addEventListener("feral-cart-updated", compute);
    window.addEventListener("storage", compute);
    return () => { window.removeEventListener("feral-cart-updated", compute); window.removeEventListener("storage", compute); };
  }, []);
  return cart;
};

const isActive = (path, to) => to !== "/" && (path === to || path.startsWith(`${to}/`) || (to === "/stays" && path.startsWith("/feral-stays")));

// Transparent over image heroes at the top; solid yellow bar once scrolled (or always when overlay=false).
export const SiteNav = ({ overlay = true }) => {
  const { pathname } = useLocation();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const cart = useCart();

  useEffect(() => {
    const on = () => setScrolled(window.scrollY > 10);
    on();
    window.addEventListener("scroll", on, { passive: true });
    return () => window.removeEventListener("scroll", on);
  }, []);
  useEffect(() => { document.body.style.overflow = open ? "hidden" : ""; }, [open]);

  const solid = !overlay || scrolled || open;
  const ink = solid ? "text-[#9b3d0a]" : "text-[#ebc754]";

  return (
    <header className={`fixed inset-x-0 top-0 z-50 transition-colors duration-300 ${solid ? "bg-[#ebc754]" : "bg-transparent"}`} data-testid="site-nav" data-state={solid ? "solid" : "transparent"}>
      <nav className="h-[72px] lg:h-[88px] xl:h-[100px] px-6 lg:px-8 xl:px-[3.75rem] flex lg:grid lg:grid-cols-[1fr_auto_1fr] items-center justify-between gap-4 xl:gap-6">
        <Link to="/" aria-label="FERAL home" data-testid="nav-logo" className="shrink-0">
          <img src={solid ? "/skin/logo-rust.svg" : "/skin/logo-yellow.svg"} alt="FERAL" className="h-8 lg:h-9 xl:h-11 w-auto" />
        </Link>
        <div className="hidden lg:flex items-center gap-5 xl:gap-8 2xl:gap-11">
          {NAV.map((n) => (
            <SmartLink key={n.label} to={n.to} data-testid={n.testid}
              className={`relative whitespace-nowrap feral-nav text-[12px] xl:text-[13px] 2xl:text-[14px] tracking-[0.06em] uppercase transition-opacity hover:opacity-70 ${ink} ${isActive(pathname, n.to) ? "after:absolute after:left-0 after:right-0 after:-bottom-2 after:h-px after:bg-current" : ""}`}>
              {n.label}
            </SmartLink>
          ))}
        </div>
        <div className={`flex items-center justify-self-end gap-5 ${ink}`}>
          {cart.count > 0 && (
            <Link to={cart.slug ? `/events/${cart.slug}` : "/events"} aria-label="Basket" data-testid="navbar-basket" className="relative">
              <ShoppingBag size={22} />
              <span className="absolute -top-2 -right-2 bg-[#9b3d0a] text-[#ebc754] text-[10px] font-bold rounded-full w-5 h-5 flex items-center justify-center">{cart.count}</span>
            </Link>
          )}
          <SmartLink to="/#breathwork" data-testid="nav-cta-free-session"
            className={`hidden sm:inline-flex items-center whitespace-nowrap feral-nav text-[12px] xl:text-[13px] 2xl:text-[14px] tracking-[0.06em] uppercase px-5 xl:px-6 h-[46px] xl:h-[52px] border transition-colors duration-300 ${solid ? "bg-[#9b3d0a] border-[#9b3d0a] text-[#ebc754] hover:bg-[#82254d] hover:border-[#82254d]" : "border-[#ebc754] text-[#ebc754] hover:bg-[#ebc754] hover:text-[#9b3d0a]"}`}>
            Get your free session
          </SmartLink>
          <button onClick={() => setOpen(!open)} aria-label="Menu" data-testid="nav-mobile-toggle" className="lg:hidden">{open ? <X size={26} /> : <Menu size={26} />}</button>
        </div>
      </nav>

      {open && (
        <div className="lg:hidden fixed inset-0 top-[72px] bg-[#ebc754] text-[#9b3d0a] px-6 pt-10 pb-10 flex flex-col" data-testid="nav-mobile-menu">
          <div className="flex flex-col gap-6">
            {NAV.map((n) => (
              <SmartLink key={n.label} to={n.to} onClick={() => setOpen(false)} className={`feral-nav text-2xl uppercase tracking-[0.04em] ${isActive(pathname, n.to) ? "underline underline-offset-8" : ""}`}>{n.label}</SmartLink>
            ))}
          </div>
          <SmartLink to="/#breathwork" onClick={() => setOpen(false)} className="mt-10 self-start feral-nav uppercase text-sm tracking-[0.06em] bg-[#9b3d0a] text-[#ebc754] px-6 py-4">Get your free session</SmartLink>
          <div className="mt-auto flex items-end justify-between">
            <p className="feral-display text-2xl">{STRAPLINE}</p>
            <div className="flex gap-4">
              <a href={FOOTER.instagram} target="_blank" rel="noopener noreferrer" aria-label="Instagram"><Instagram size={20} /></a>
            </div>
          </div>
        </div>
      )}
    </header>
  );
};
